import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { OrderDetail } from '@/lib/api/orders';
import { formatMoney } from "@/lib/money";
import { TransactionsList, TransactionItemData } from '@/components/transactions';

interface OrderRefundHistoryCardProps {
  order: OrderDetail;
}

// Get all refund transactions from all payments
function getRefundTransactions(order: OrderDetail): TransactionItemData[] {
  return (
    order.payments
      ?.flatMap((payment) => {
        if (!payment.transactions) return [];
        return payment.transactions
          .filter((transaction) => transaction.type === 'refund')
          .map((transaction): TransactionItemData => {
            // Cast to any to access all fields
            const txn = transaction as any;

            return {
              // Spread ALL fields first to preserve everything from backend
              ...txn,
              // Then format specific fields that need conversion
              createdAt:
                transaction.createdAt instanceof Date
                  ? transaction.createdAt.toISOString()
                  : String(transaction.createdAt),
            };
          });
      })
      ?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) || []
  );
}

export function OrderRefundHistoryCard({ order }: OrderRefundHistoryCardProps) {
  const { t } = useTranslation('orders');
  const refundTransactions = getRefundTransactions(order);

  if (refundTransactions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('refundHistory')}</CardTitle>
          <CardDescription>{t('noRefunds')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const totalRefunded = refundTransactions
    .filter((t) => t.status === 'succeeded')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('refundHistory')}</CardTitle>
        <CardDescription>
          {refundTransactions.length} {refundTransactions.length !== 1 ? t('refunds') : t('refund')} • Total
          refunded: {formatMoney(totalRefunded, order.currency || 'USD')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <TransactionsList
          transactions={refundTransactions}
          currency={order.currency || 'USD'}
          emptyMessage={t('noRefunds')}
          expandable={true}
        />
      </CardContent>
    </Card>
  );
}
